import {useMemo} from 'react';
import {Layer, Source} from 'react-map-gl/maplibre';

type PolygonPoint = [number, number];

type PolygonTone = 'default' | 'pin';

type PolygonObject = {
  id?: string | number;
  points?: PolygonPoint[];
  isActivated?: boolean;
  polygonColor?: string;
  polygonTone?: PolygonTone;
};

type PolygonDataItem = PolygonObject | PolygonPoint[];

type PolygonProps = {
  data?: PolygonDataItem[];
  selectedPolygonId?: string | number;
  selectedPolygonIds?: Array<string | number>;
};

const isPolygonObject = (item: PolygonDataItem): item is PolygonObject => !Array.isArray(item);

const readThemeColor = (token: string) => {
  if (typeof document === 'undefined') {
    return '';
  }

  return getComputedStyle(document.documentElement).getPropertyValue(token).trim();
};

const Polygon = ({data, selectedPolygonId}: PolygonProps) => {
  const polygonToneColors = useMemo<Record<PolygonTone, string>>(
    () => ({
      default: readThemeColor('--color-blue-main'),
      pin: readThemeColor('--color-yellow-main')
    }),
    []
  );
  const polygonMutedColor = useMemo(() => readThemeColor('--color-gray-07'), []);

  const polygonsGeoJson = useMemo(
    () => ({
      type: 'FeatureCollection',
      features:
        data?.map((item) => {
          let coordinates;
          if (isPolygonObject(item) && item.points && item.points.length) {
            coordinates = [item.points.map((point) => [point[1], point[0]])];
          } else if (Array.isArray(item)) {
            coordinates = [item.map((point) => [point[1], point[0]])];
          } else {
            coordinates = [[]];
          }

          const polygonColor = isPolygonObject(item)
            ? item.polygonColor || polygonToneColors[item.polygonTone ?? 'default']
            : polygonToneColors.default;
          const itemId = isPolygonObject(item) ? item.id : undefined;

          return {
            type: 'Feature',
            properties: {
              id: itemId,
              isActivated: isPolygonObject(item) && item.isActivated ? item.isActivated : false,
              isSelected: selectedPolygonId === itemId,
              polygonColor: polygonColor
            },
            geometry: {
              type: 'Polygon',
              coordinates
            }
          };
        }) || []
    }),
    [data, polygonToneColors, selectedPolygonId]
  );

  const polygonFillLayer = {
    id: 'custom-polygon-fill',
    type: 'fill',
    paint: {
      'fill-color': [
        'case',
        ['==', ['get', 'isSelected'], true],
        ['get', 'polygonColor'],
        ['==', ['get', 'isActivated'], true],
        ['get', 'polygonColor'],
        polygonMutedColor
      ],
      'fill-opacity': [
        'case',
        ['==', ['get', 'isSelected'], true],
        0,
        ['==', ['get', 'isActivated'], true],
        0.1,
        0.1
      ]
    },
    layout: {
      visibility: 'visible'
    }
  };

  const polygonOutlineLayer = {
    id: 'custom-polygon-outline',
    type: 'line',
    paint: {
      'line-color': [
        'case',
        ['==', ['get', 'isSelected'], true],
        ['get', 'polygonColor'],
        ['==', ['get', 'isActivated'], true],
        ['get', 'polygonColor'],
        polygonMutedColor
      ],
      'line-width': [
        'case',
        ['==', ['get', 'isSelected'], true],
        0,
        ['==', ['get', 'isActivated'], true],
        2,
        1
      ]
    }
  };

  const polygonHighlightLayer = {
    id: 'custom-polygon-highlight',
    type: 'fill',
    paint: {
      'fill-color': '#0059ff',
      'fill-opacity': 0.2
    },
    filter: ['==', ['get', 'isSelected'], true],
    layout: {
      visibility: 'visible'
    }
  };

  const polygonHighlightOutlineLayer = {
    id: 'custom-polygon-highlight-outline',
    type: 'line',
    paint: {
      'line-color': '#0059ff',
      'line-width': 2
    },
    filter: ['==', ['get', 'isSelected'], true],
    layout: {
      visibility: 'visible'
    }
  };

  return (
    <Source id="custom-polygon" type="geojson" data={polygonsGeoJson as any}>
      <Layer {...(polygonFillLayer as any)} />
      <Layer {...(polygonOutlineLayer as any)} />
      <Layer {...(polygonHighlightLayer as any)} />
      <Layer {...(polygonHighlightOutlineLayer as any)} />
    </Source>
  );
};

export default Polygon;
